import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { Appointment } from 'src/app/pages/appointment/iappointment';
import { PatientService } from 'src/app/pages/patient/patient.service';
import { UserService } from 'src/app/pages/user/user.service';
import { PatientReportPrintComponent } from '../patient-report-print.component';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { Iopd } from 'src/app/pages/opd/opddetails/iopddetails';
import { IHospitalInfo } from 'src/app/pages/hospital/ihospital-info';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { IPatientInfo } from 'src/app/pages/patient/ipatient-info';
import { IUsers } from 'src/app/pages/user/iusers';
import { GenderList } from 'src/app/common/types/common';
import { IPrescription } from 'src/app/pages/opd/prescription/iprescription';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-patient-report-viewer',
  imports: [CommonModule, MaterialModule, TranslateModule, TablerIconsModule, PatientReportPrintComponent, FormsModule],
  providers: [DatePipe],
  templateUrl: './patient-report-viewer.component.html',
  styleUrl: './patient-report-viewer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PatientReportViewerComponent implements OnInit, OnDestroy {

  @ViewChild(PatientReportPrintComponent) reportPrintComponent!: PatientReportPrintComponent;

  reportData: Appointment;
  isDialog: boolean = false;
  doctorInfo: IUsers;
  patientDetails: IPatientInfo;
  hospitalInfo: IHospitalInfo;
  @Input() appointmentData: Appointment;
  @Input() OpdData: Iopd | null = null;
  printTitle: string;
  private pendingLoads: number = 0;
  isLoading: boolean = true;
  groupedReports: {
    categoryType: string;
    categories: {
      category: string;
      subCategories: {
        subCategory: string;
        items: any[];
      }[];
    }[];
  }[] = [];
  Prescriptions: IPrescription[] = [];
  noData = false;
  @Input() showCategory: boolean = true;
  private destroy$ = new Subject<void>();

  constructor(
    private patientService: PatientService,
    @Inject(MAT_DIALOG_DATA) @Optional() private _data: { appointmentDetails: Appointment, isDialog: true } | null,
    private userService: UserService,
    @Optional() private dialogRef: MatDialogRef<PatientReportPrintComponent>,
    private translate: TranslateService,
    private hospitalState: HospitalStateService
  ) {
    this.reportData = this._data?.appointmentDetails ?? this.appointmentData;
  }

  ngOnInit(): void {
    this.isDialog = this._data?.isDialog ?? false;

    this.reportData = {
      ...(this._data?.appointmentDetails ?? this.appointmentData)
    };

    if (this.OpdData) {
      this.reportData = {
        ...this.reportData,
        patientName: this.OpdData.patientName,
        opdNumber: this.OpdData.opdNumber
      };
    }

    this.printTitle =
      this.translate.instant('ENTITY.PATIENT') +
      this.translate.instant('LABEL.REPORT');

    this.hospitalInfo = this.hospitalState.getHospital();

    this.loadDoctorAndPatient();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCloseDialog() {
    this.dialogRef.close();
  }

  getGenderName(value?: string): string {
    const gender = GenderList.find(g => g.value === value);
    return gender ? gender.name : '';
  }

  onDataLoaded() {
    this.isLoading = false;
    this.Prescriptions = this.reportPrintComponent.Prescriptions;
    this.noData = this.reportPrintComponent.noDataAvailable;
  }

  printReportDirect() {
    this.reportPrintComponent.printReportDirect();
  }

  printReportOnly() {
    this.reportPrintComponent.printReportOnly();
  }

  printPrescriptionOnly() {
    this.reportPrintComponent.printPrescriptionOnly();
  }

  get noDataAvailable(): boolean {
    return this.noData;
  }

  private loadDoctorAndPatient(): void {
    if (!this.reportData?.doctorId || !this.reportData?.patientId) {
      this.isLoading = false;
      return;
    }

    forkJoin({
      doctor: this.userService.getUserByID(Number(this.reportData.doctorId)),
      patient: this.patientService.getPatientByID(Number(this.reportData.patientId))
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ doctor, patient }) => {
          this.doctorInfo = doctor.result;
          this.patientDetails = patient.result;
        },
        complete: () => {
          this.isLoading = false;
        }
      });
  }
}
